/**
 * CallbackWidget — bottom-LEFT floating button + modal.
 *
 * Sits in the opposite corner from the existing AIChatWidget (which is
 * bottom-right). On click, opens a centered modal with Name / Email /
 * Phone + "Request a Call Back" CTA. Posts to /api/popup/capture with
 * variant_id='callback_request' so the existing popup_leads pipeline
 * (drip campaign + lead_contacts + admin notification + SMS to Larry)
 * fires automatically.
 *
 * Mount once at the App.js root. Hidden on admin/portal/auth pages.
 */
import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Phone, X, Loader2, CheckCircle2 } from 'lucide-react';
import { validateEmail, normalizeEmail } from '../utils/emailValidation';

const API = process.env.REACT_APP_BACKEND_URL;

const EXCLUDED_PATHS = [
  '/admin', '/login', '/ai-visibility-trial', '/warm-handoffs',
  '/partner/hq', '/partner/install', '/affiliate/dashboard',
  '/client-portal', '/client', '/ppc-portal', '/client-pay',
];

// Language-pack so the widget mirrors the page locale (/es/* shows Spanish)
const T = {
  en: {
    aria: 'Talk with us now',
    fab: 'Talk with us now!',
    title: 'Would you like us to call you back?',
    sub: 'Free call within 2 business hours — no obligation',
    close: 'Close',
    okHead: "Got it — we'll call shortly",
    okBody: (phone) => (
      <>
        Larry just got the alert. Expect a call from a <strong>{phone}</strong> Dallas number within 2 business hours.
      </>
    ),
    lblName: 'Your name',
    lblEmail: 'Email',
    lblPhone: 'Phone',
    phName: 'Jane Doe',
    phEmail: 'jane@example.com',
    phPhone: '(214) 555-0100',
    errName: 'Please enter your name',
    errEmail: 'Please enter a valid email',
    errPhone: 'Please enter a valid phone number',
    sending: 'Sending…',
    cta: 'Request a Call Back',
    fineprint: 'By submitting, you agree to receive a call from Web-TechServices. We never spam — promise.',
    trust1: 'in business',
    trust2: 'projects',
    trust3: 'avg rating',
    network: 'Network error',
    generic: 'Something went wrong. Please try again.',
  },
  es: {
    aria: '¡Habla con nosotros ahora!',
    fab: '¡Habla con nosotros ahora!',
    title: '¿Quiere que le devolvamos la llamada?',
    sub: 'Llamada gratis en menos de 2 horas hábiles — sin compromiso',
    close: 'Cerrar',
    okHead: 'Listo — le llamamos en breve',
    okBody: (phone) => (
      <>
        Larry ya recibió la alerta. Espere una llamada desde un número de Dallas <strong>{phone}</strong> en menos de 2 horas hábiles.
      </>
    ),
    lblName: 'Su nombre',
    lblEmail: 'Correo electrónico',
    lblPhone: 'Teléfono',
    phName: 'Juan Pérez',
    phEmail: 'juan@ejemplo.com',
    phPhone: '(214) 555-0100',
    errName: 'Por favor escriba su nombre',
    errEmail: 'Por favor escriba un correo válido',
    errPhone: 'Por favor escriba un teléfono válido',
    sending: 'Enviando…',
    cta: 'Solicitar Llamada',
    fineprint: 'Al enviar, usted acepta recibir una llamada de Web-TechServices. Nunca enviamos spam — palabra.',
    trust1: 'en el negocio',
    trust2: 'proyectos',
    trust3: 'calificación',
    network: 'Error de red',
    generic: 'Algo salió mal. Inténtelo de nuevo.',
  },
};

export default function CallbackWidget() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const i18nLang = (i18n.language || 'en').split('-')[0];
  const isEs = pathname.startsWith('/es') || i18nLang === 'es';
  const t = isEs ? T.es : T.en;
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // Spam protection
  const honeypotRef = useRef(null);
  const formLoadedAt = useRef(Date.now());

  // Re-arm form when modal closes
  useEffect(() => {
    if (!open) {
      // small delay so the success state stays visible
      const t = setTimeout(() => {
        setSubmitted(false);
        setError(null);
      }, 600);
      return () => clearTimeout(t);
    }
    formLoadedAt.current = Date.now();
  }, [open]);

  // Allow the mobile header "Call" button to open this widget.
  useEffect(() => {
    const openWidget = () => setOpen(true);
    window.addEventListener('wts:open-callback', openWidget);
    return () => window.removeEventListener('wts:open-callback', openWidget);
  }, []);

  // Hide on excluded pages
  if (EXCLUDED_PATHS.some((p) => pathname.startsWith(p))) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const nm = name.trim();
    const em = normalizeEmail(email);
    const ph = phone.trim();
    if (!nm || nm.length < 2) return setError(t.errName);
    if (!em) return setError(t.errEmail);
    const emailErr = validateEmail(em);
    if (emailErr) return setError(emailErr);
    if (!ph || ph.replace(/\D/g, '').length < 7) return setError(t.errPhone);
    if (honeypotRef.current?.value) return; // bot

    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/popup/capture`, {
        name: nm,
        email: em,
        phone: ph,
        variant_id: 'callback_request',
        page_url: window.location.pathname,
        website: honeypotRef.current?.value || '',
        form_loaded_at: formLoadedAt.current,
        language: isEs ? 'es' : 'en',
      }, { timeout: 20000 });
      if (res.data?.success) {
        setSubmitted(true);
        setName(''); setEmail(''); setPhone('');
        // Auto-close after success state has been seen
        setTimeout(() => setOpen(false), 4000);
      } else {
        setError(res.data?.message || t.generic);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || t.network);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating button — bottom-LEFT (mirror of AIChatWidget) */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label={t.aria}
          data-testid="callback-widget-fab"
          className="hidden xl:flex fixed bottom-24 left-4 z-50 group items-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-full shadow-2xl hover:scale-105 hover:shadow-blue-500/40 transition-all duration-200"
          style={{ animation: 'cbw-pulse 2.4s ease-in-out infinite' }}
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
          </span>
          <Phone className="h-4 w-4" />
          <span className="font-semibold text-sm">{t.fab}</span>
        </button>
      )}

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-fadeIn"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cbw-title"
          data-testid="callback-widget-modal"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          {/* Card */}
          <div
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-slideUp"
            data-testid="callback-widget-card"
          >
            {/* Close */}
            <button
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 p-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 z-10"
              aria-label={t.close}
              data-testid="callback-widget-close"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Header band */}
            <div className="bg-gradient-to-br from-blue-600 to-cyan-600 px-6 pt-7 pb-6 text-white text-center">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-white/20 backdrop-blur-md mb-3">
                <Phone className="h-7 w-7" />
              </div>
              <h2 id="cbw-title" className="text-xl font-bold leading-snug">
                {t.title}
              </h2>
              <p className="text-sm text-blue-100 mt-1">
                {t.sub}
              </p>
            </div>

            {/* Body */}
            <div className="p-6">
              {submitted ? (
                <div className="text-center py-4" data-testid="callback-widget-success">
                  <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-slate-900 mb-1">{t.okHead}</h3>
                  <p className="text-sm text-slate-600">
                    {t.okBody('(214)')}
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3" data-testid="callback-widget-form">
                  {/* Honeypot */}
                  <input
                    ref={honeypotRef}
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    className="hidden"
                    aria-hidden="true"
                  />
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{t.lblName}</span>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t.phName}
                      autoFocus
                      autoComplete="name"
                      required
                      className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      data-testid="callback-widget-name"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{t.lblEmail}</span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t.phEmail}
                      autoComplete="email"
                      required
                      className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      data-testid="callback-widget-email"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{t.lblPhone}</span>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder={t.phPhone}
                      autoComplete="tel"
                      required
                      className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      data-testid="callback-widget-phone"
                    />
                  </label>

                  {error && (
                    <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded" data-testid="callback-widget-error">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={busy}
                    className="w-full mt-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold py-3 rounded-lg hover:shadow-lg hover:shadow-blue-500/30 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                    data-testid="callback-widget-submit"
                  >
                    {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> {t.sending}</> : <><Phone className="h-4 w-4" /> {t.cta}</>}
                  </button>

                  <p className="text-[11px] text-slate-500 text-center pt-1">
                    {t.fineprint}
                  </p>
                </form>
              )}

              {/* Trust strip */}
              <div className="mt-5 pt-4 border-t border-slate-200 grid grid-cols-3 gap-2 text-center text-[11px] text-slate-600">
                <div><span className="block font-bold text-slate-900 text-sm">25+ {isEs ? 'años' : 'yrs'}</span>{t.trust1}</div>
                <div><span className="block font-bold text-slate-900 text-sm">2,500+</span>{t.trust2}</div>
                <div><span className="block font-bold text-slate-900 text-sm">⭐ 4.9</span>{t.trust3}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Local CSS for the gentle pulse */}
      <style>{`
        @keyframes cbw-pulse {
          0%, 100% { box-shadow: 0 10px 30px -10px rgba(37,99,235,0.45), 0 0 0 0 rgba(37,99,235,0.4); }
          50%      { box-shadow: 0 10px 30px -10px rgba(37,99,235,0.65), 0 0 0 10px rgba(37,99,235,0); }
        }
        @keyframes cbw-fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes cbw-slideUp { from { opacity: 0; transform: translateY(20px) scale(0.96); } to { opacity: 1; transform: none; } }
        .animate-fadeIn { animation: cbw-fadeIn 0.18s ease-out; }
        .animate-slideUp { animation: cbw-slideUp 0.22s cubic-bezier(0.16, 1, 0.3, 1); }
      `}</style>
    </>
  );
}
